import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GripVertical, PencilLine, Plus, ArrowLeft, Upload, Search, Download, FileSpreadsheet, FileText, ClipboardCheck } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { cn } from '../../../lib/utils';
import {
  xxLargeTextSize,
  extraLargeTextSize,
  mediumText,
  primaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  regularText,
  roundedLG,
  cardClass,
  iconGreenAccent,
} from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useAppSettingsStore } from '../../../stores/appSettingsStore';
import { CustomIngredient, fetchCustomIngredientsByRestaurant, deleteCustomIngredient } from '../../../services/customIngredients/customIngredientsService';
import { Subrecipe, fetchSubrecipesByRestaurant } from '../../../services/subrecipes/subrecipesService';
import { CostPriceCategory, fetchCostPriceCategoriesByRestaurant, createCostPriceCategory, updateCostPriceCategory, deleteCostPriceCategory, updateCategoryPositions, CostPriceCategoryDuplicateNameApiError } from '../../../services/costPrice/costPriceCategoriesService';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { eur } from '../../orderhistory/utils';
import { getCategoryStyles } from '../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../orderhistory/sections/FilterControls';
import { calculateSubrecipeUnitCost } from '../../../utils/costPriceUtils';
import { applyWaste, resolveWaste, resolveWastePercentage } from '../../../utils/wasteUtils';
import { getPortionsUnitDisplay, formatSubrecipeUnitPrice } from '../../../utils/ingredientGrouping';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { TabbedSections } from '../../ui/TabbedSections';
import UploadSupplierPriceFileModal from './modals/UploadSupplierPriceFileModal';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessFileBatchCustomIngredients, canAccessFileBatchSubrecipes, canAccessCostPriceAutomation } from '../../../services/accessManagement/accessManagement';
import UploadSubrecipeTemplateModal from './modals/UploadSubrecipeTemplateModal';
import { buildSubrecipesCsv, buildSubrecipesTxt, subrecipesExportFilename, SubrecipesExportContext } from './new/subrecipesExport';
import { downloadCsv } from '../../../utils/reports/utilizationCsv';
import { downloadTextFile } from '../revenueHistory/revenueItemsExport';
import CustomIngredientDetailModal from './new/CostPrice/CustomIngredientDetailModal';
import SubrecipeDetailModal from './new/CostPrice/SubrecipeDetailModal';
import EditCategoryModal from './modals/EditCategoryModal';
import DeleteSubrecipeEntityModal from './modals/DeleteSubrecipeEntityModal';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import { SortableList } from '../../common/SortableList';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useSubrecipeReviewStore } from '../../../stores/appSubrecipeReviewStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { MobileCardList } from '../../shared/mobile/ResponsiveTable';

// On mobile the ingredient/subrecipe lists render incrementally (load-more)
// instead of mapping the whole array into the DOM — these lists can be large.
const MOBILE_BATCH = 20;

// Search box used by the Subrecipes and Custom Ingredients tabs — same styling
// as the recipes page search input.
const ListSearchInput = ({ value, onChange, placeholder }: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => (
  <div className="relative w-full sm:w-[260px]">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full h-10 pl-10 pr-4 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
        "bg-white dark:bg-background-dark",
        "text-[14px] leading-5 text-[#344054] dark:text-textSecondary-dark",
        "placeholder:text-[#667085] dark:placeholder:text-textTertiary-dark",
        "focus:outline-none focus:ring-2 focus:ring-[#0E8965]/20 dark:focus:ring-[#0E8965]/20 focus:border-[#0E8965]",
        "transition-colors"
      )}
    />
  </div>
);

// A draggable (non-default) category card on the Categories tab. Drag handle +
// swatch + name + edit/delete, reusing the same dnd-kit pattern as the
// category-assignment modal.
const SortableCategoryCard = ({ category, onEdit, onDelete }: {
  category: CostPriceCategory;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore(s => s.darkMode);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(cardClass, "p-4")}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            {...attributes}
            {...listeners}
            type="button"
            className="touch-none cursor-grab active:cursor-grabbing flex-shrink-0"
            aria-label="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
          </button>
          <div
            className="w-4 h-4 rounded flex-shrink-0 border border-gray-200 dark:border-gray-700"
            style={{ backgroundColor: category.color || '#2563EB' }}
          />
          <div className="flex-1 min-w-0">
            <div className={cn(mediumText, primaryText)}>{category.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className={cn(
              "w-9 h-9 flex items-center justify-center border border-[1px] border-[#0E8965] dark:border-darkGreenColor-dark",
              "rounded-full bg-white dark:bg-card-dark transition-colors",
              "hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10"
            )}
            aria-label="Edit category"
          >
            <PencilLine className="w-4 h-4" color={darkMode ? iconGreenAccent.dark : iconGreenAccent.light} />
          </button>
          <button
            onClick={onDelete}
            className={cn(
              smallTextSize,
              mediumText,
              "px-3 py-2 flex items-center justify-center rounded-xl border border-[#D92D20] dark:border-red-500 hover:bg-[#FEF3F2] dark:hover:bg-red-500/10 text-[#D92D20] dark:text-textRed-dark transition-colors"
            )}
            aria-label="Delete category"
          >
            {t('costPrice.subrecipes.removeAction')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageIngredientsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const darkMode = useAppSettingsStore(s => s.darkMode);

  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const orderHistoryStore = useOrderHistory();
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const reloadSubrecipesStore = useSubrecipesStore(s => s.reload);
  const reloadCostPriceStore = useCostPriceStore(s => s.reload);
  const isCostPriceLoadedFor = useCostPriceStore(s => s.isDataLoadedFor);


  // Local state
  const [customIngredients, setCustomIngredients] = useState<CustomIngredient[]>([]);
  const [subrecipes, setSubrecipes] = useState<Subrecipe[]>([]);
  const [categories, setCategories] = useState<CostPriceCategory[]>([]);
  // Which tab is showing. Subrecipes is the default landing tab.
  const [activeTab, setActiveTab] = useState<'subrecipes' | 'customIngredients' | 'categories'>('subrecipes');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Restaurant-wide default waste %, applied to displayed subrecipe ingredient
  // costs — read from the cached restaurant store, no per-page fetch.
  const reportSettingsMap = useRestaurantStore(s => s.reportSettings);
  const defaultWastePercentage = resolveWastePercentage(
    restaurantId && !isAllRestaurants ? reportSettingsMap[restaurantId]?.wastePercentage : undefined
  );

  // Supplier price file import (gated by account feature)
  const { backendUser, accountSettings } = useAuth();
  const canImportPriceFile = canAccessFileBatchCustomIngredients(backendUser, accountSettings);
  const canImportSubrecipeFile = canAccessFileBatchSubrecipes(backendUser, accountSettings);
  // Subrecipe import review: restore an unfinished review for this restaurant from
  // sessionStorage (a report for another restaurant is ignored).
  const subrecipeReviewReport = useSubrecipeReviewStore(s => s.reviewReport);
  const subrecipeReviewStatuses = useSubrecipeReviewStore(s => s.statuses);
  const hydrateSubrecipeReview = useSubrecipeReviewStore(s => s.hydrateForRestaurant);
  useEffect(() => {
    if (restaurantId && !isAllRestaurants) {
      hydrateSubrecipeReview(restaurantId);
    }
  }, [restaurantId, isAllRestaurants, hydrateSubrecipeReview]);
  // Gated on the same account feature as the import itself: an account that lost
  // the feature mid-review should not be nudged back into it.
  const hasResumableSubrecipeReview = canImportSubrecipeFile
    && !!subrecipeReviewReport
    && subrecipeReviewReport.restaurantId === restaurantId
    && subrecipeReviewReport.subrecipes.length > 0;
  const reviewedSubrecipeCount = subrecipeReviewReport
    ? subrecipeReviewReport.subrecipes.filter(item => subrecipeReviewStatuses[item.subrecipeId] && subrecipeReviewStatuses[item.subrecipeId] !== 'pending').length
    : 0;
  // Subrecipe export sits behind the same permission as the cost price page's export menu.
  const canExportSubrecipes = canAccessCostPriceAutomation(backendUser, accountSettings);
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  // Free-text search per tab (lists get long once a restaurant is fully set up).
  const [subrecipeSearch, setSubrecipeSearch] = useState('');
  const [customIngredientSearch, setCustomIngredientSearch] = useState('');
  // Subrecipe export dropdown (CSV / TXT).
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement | null>(null);

  // Close the export dropdown on outside click or Escape.
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showExportMenu]);

  // Modals
  const [showCreateCustomIngredientModal, setShowCreateCustomIngredientModal] = useState(false);
  const [showUploadPriceFileModal, setShowUploadPriceFileModal] = useState(false);
  const [showUploadSubrecipeTemplateModal, setShowUploadSubrecipeTemplateModal] = useState(false);
  const [showCreateSubrecipeModal, setShowCreateSubrecipeModal] = useState(false);
  const [editingCustomIngredient, setEditingCustomIngredient] = useState<CustomIngredient | null>(null);
  const [editingSubrecipeId, setEditingSubrecipeId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<{ id: string; name: string; color: string } | null>(null);
  const [deletingCustomIngredient, setDeletingCustomIngredient] = useState<{ id: string; name: string } | null>(null);
  const [deletingSubrecipe, setDeletingSubrecipe] = useState<{ id: string; name: string } | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<{ id: string; name: string } | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!restaurantId || isAllRestaurants) return;

      setIsLoading(true);
      setError(null);

      try {
        // Load order history for price calculation
        if (!orderHistoryStore.isDataLoadedFor(restaurantId)) {
          await orderHistoryStore.load(restaurantId);
        }

        const [customIngs, subs, cats] = await Promise.all([
          fetchCustomIngredientsByRestaurant(restaurantId),
          fetchSubrecipesByRestaurant(restaurantId),
          fetchCostPriceCategoriesByRestaurant(restaurantId)
        ]);

        setCustomIngredients(customIngs);
        setSubrecipes(subs);
        setCategories(cats.sort((a, b) => a.position - b.position));
      } catch (err) {
        console.error('Failed to load ingredients:', err);
        setError('Failed to load ingredients');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);

  // How many cards the mobile (load-more) lists currently render. Reset to the
  // first batch whenever the underlying data or the active search/filter changes.
  const [customVisibleCount, setCustomVisibleCount] = useState(MOBILE_BATCH);
  const [subrecipeVisibleCount, setSubrecipeVisibleCount] = useState(MOBILE_BATCH);
  useEffect(() => { setCustomVisibleCount(MOBILE_BATCH); }, [customIngredientSearch, supplierFilter, customIngredients]);
  useEffect(() => { setSubrecipeVisibleCount(MOBILE_BATCH); }, [subrecipeSearch, subrecipes]);

  const handleDeleteCustomIngredient = (id: string, name: string) => {
    setDeletingCustomIngredient({ id, name });
  };
  
  const handleDeleteCustomIngredientConfirm = async () => {
    if (!deletingCustomIngredient) return;
    
    try {
      await deleteCustomIngredient(deletingCustomIngredient.id);
      setCustomIngredients(prev => prev.filter(ing => ing.id !== deletingCustomIngredient.id));
      setDeletingCustomIngredient(null);
    } catch (error) {
      console.error('Failed to delete custom ingredient:', error);
      alert('Failed to delete custom ingredient');
    }
  };
  
  const handleDeleteSubrecipe = (id: string, name: string) => {
    setDeletingSubrecipe({ id, name });
  };

  const handleSubrecipeDeleted = async () => {
    const deletedId = deletingSubrecipe?.id;
    setDeletingSubrecipe(null);
    if (deletedId) {
      setSubrecipes(prev => prev.filter(sub => sub.id !== deletedId));
    }
    await reloadSubrecipesStore();
    if (restaurantId && isCostPriceLoadedFor(restaurantId)) {
      await reloadCostPriceStore();
    }
  };
  
  // Refresh after a create/edit save from the side modal: re-fetch the page's
  // subrecipe list and keep the shared stores in sync (costs may have changed).
  const handleSubrecipeSaved = async () => {
    if (restaurantId) {
      const updatedSubrecipes = await fetchSubrecipesByRestaurant(restaurantId);
      setSubrecipes(updatedSubrecipes);
    }
    await reloadSubrecipesStore();
    if (restaurantId && isCostPriceLoadedFor(restaurantId)) {
      await reloadCostPriceStore();
    }
  };
  
  const handleIngredientsReloaded = async () => {
    if (restaurantId) {
      const [customIngs, subs] = await Promise.all([
        fetchCustomIngredientsByRestaurant(restaurantId),
        fetchSubrecipesByRestaurant(restaurantId)
      ]);
      setCustomIngredients(customIngs);
      setSubrecipes(subs);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim() || !restaurantId) return;
    try {
      await createCostPriceCategory({ restaurantId, name: newCategoryName.trim(), scope: 'revenue' });
      const cats = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      setCategories(cats.sort((a, b) => a.position - b.position));
      setNewCategoryName('');
    } catch (err) {
      console.error('Failed to create category:', err);
      setError(err instanceof CostPriceCategoryDuplicateNameApiError
        ? t('costPrice.manageIngredients.duplicateCategoryNameError')
        : t('costPrice.manageIngredients.createCategoryFailed'));
    }
  };

  // Persist a drag reorder of the non-default categories (index → position),
  // mirroring the category-assignment modal's reorder handler.
  const handleReorderCategories = async (reordered: CostPriceCategory[]) => {
    if (!restaurantId) return;
    const updates = reordered.map((cat, index) => ({ id: cat.id, position: index }));
    // Optimistic local reorder so the drag feels instant.
    setCategories(prev => {
      const posById = new Map(updates.map(u => [u.id, u.position]));
      return [...prev]
        .map(c => (posById.has(c.id) ? { ...c, position: posById.get(c.id)! } : c))
        .sort((a, b) => a.position - b.position);
    });
    try {
      await updateCategoryPositions(updates);
      const cats = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      setCategories(cats.sort((a, b) => a.position - b.position));
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      // Re-sync from server on failure.
      const cats = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      setCategories(cats.sort((a, b) => a.position - b.position));
    }
  };

  const handleCategoryUpdate = async () => {
    if (!editingCategory || !restaurantId) return;

    try {
      await updateCostPriceCategory({
        id: editingCategory.id,
        name: editingCategory.name,
        color: editingCategory.color
      });

      // Reload categories
      const cats = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      setCategories(cats.sort((a, b) => a.position - b.position));
      setEditingCategory(null);
    } catch (error) {
      console.error('Failed to update category:', error);
      // Alert (not the page banner) so the message shows above the still-open
      // edit modal, letting the user correct the name in place.
      alert(error instanceof CostPriceCategoryDuplicateNameApiError
        ? t('costPrice.manageIngredients.duplicateCategoryNameError')
        : t('costPrice.manageIngredients.updateCategoryFailed'));
    }
  };
  
  const handleDeleteCategoryConfirm = async () => {
    if (!deletingCategory || !restaurantId) return;

    try {
      await deleteCostPriceCategory(deletingCategory.id);
      const cats = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      setCategories(cats.sort((a, b) => a.position - b.position));
      setDeletingCategory(null);
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  // Helper function to format custom ingredient unit price
  const formatCustomIngredientPrice = (ingredient: CustomIngredient) => {
    const base = showExVatOrders ? ingredient.price : (ingredient.priceWithTaxes ?? ingredient.price);
    const pricePerUnit = base / ingredient.quantityInfo.stdAmount;

    if (ingredient.quantityInfo.stdUnit === 'g') {
      return `${eur(pricePerUnit * 1000)}/kg`;
    } else if (ingredient.quantityInfo.stdUnit === 'ml') {
      return `${eur(pricePerUnit * 1000)}/l`;
    } else {
      return `${eur(pricePerUnit)}/${ingredient.quantityInfo.stdUnit}`;
    }
  };

  // Helper function to get current price for an ingredient
  const getCurrentPrice = (articleId: string, platform: string, isCustom: boolean) => {
    if (isCustom) {
      const customIng = customIngredients.find(ci => ci.articleId === articleId && ci.platform === platform);
      if (customIng) {
        const base = showExVatOrders ? customIng.price : (customIng.priceWithTaxes ?? customIng.price);
        return base / customIng.quantityInfo.stdAmount;
      }
    }

    // Find latest price from order history by flattening productData
    const matchingProducts: Array<{ product: any; orderDate: Date }> = [];

    for (const order of orderHistoryStore.orderHistoryList) {
      if (order.platform === platform) {
        for (const product of order.productData) {
          if (product.articleId === articleId) {
            matchingProducts.push({ product, orderDate: order.orderDate });
          }
        }
      }
    }

    // Sort by order date, most recent first
    matchingProducts.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());

    if (matchingProducts.length > 0) {
      const latestProduct = matchingProducts[0].product;
      // Standard convention (matches costPriceUtils / AddIngredients / analytics):
      // price is ex-VAT, priceWithTaxes is incl-VAT.
      const basePrice = showExVatOrders
        ? latestProduct.price
        : (latestProduct.priceWithTaxes ?? latestProduct.price);

      if (latestProduct.quantityInfo) {
        return basePrice / (latestProduct.quantityInfo.stdAmount * latestProduct.quantityInfo.stdMultiplier);
      }
      return basePrice;
    }

    return null;
  };

  // Calculate total cost for a subrecipe — surface ingredients + recursively
  // expanded nested subrecipes (via calculateSubrecipeUnitCost).
  const subrecipesById = new Map(subrecipes.map((s) => [s.id, s]));
  const calculateSubrecipeCost = (subrecipe: Subrecipe) => {
    let totalCost = 0;
    for (const ingredient of subrecipe.ingredients) {
      if (ingredient.nestedSubrecipeId) {
        const child = subrecipesById.get(ingredient.nestedSubrecipeId);
        if (!child) continue;
        const childUnitCost = calculateSubrecipeUnitCost(
          child,
          orderHistoryStore.orderHistoryList,
          customIngredients,
          showExVatOrders,
          subrecipesById,
          defaultWastePercentage
        );
        totalCost += childUnitCost * (ingredient.nestedSubrecipePortionsNeeded ?? 0);
        continue;
      }
      if (!ingredient.articleId || ingredient.quantityNeeded === undefined) continue;
      const currentPrice = getCurrentPrice(ingredient.articleId, ingredient.platform!, ingredient.isCustom ?? false);
      if (currentPrice) {
        const wastedQty = applyWaste(ingredient.quantityNeeded, resolveWaste(ingredient.wastePercentage, defaultWastePercentage));
        totalCost += currentPrice * wastedQty;
      }
    }
    return totalCost;
  };

  // Distinct supplier names (customPlatformName) for the filter dropdown.
  const supplierNames = Array.from(
    new Set(customIngredients.map(ci => ci.customPlatformName).filter((n): n is string => !!n))
  ).sort((a, b) => a.localeCompare(b));
  const customIngredientQuery = customIngredientSearch.trim().toLowerCase();
  const filteredCustomIngredients = customIngredients
    .filter(ci => supplierFilter === 'all' || ci.customPlatformName === supplierFilter)
    .filter(ci => {
      if (!customIngredientQuery) return true;
      // Search name, article id and supplier — the three things printed on the card.
      return [ci.productName, ci.articleId, ci.customPlatformName, ci.platform]
        .some(field => field?.toLowerCase().includes(customIngredientQuery));
    });

  // Resolve nested-subrecipe references to their names for the card ingredient list.
  const subrecipeNameById = new Map(subrecipes.map(s => [s.id, s.name]));

  const subrecipeQuery = subrecipeSearch.trim().toLowerCase();
  const filteredSubrecipes = subrecipes.filter(sub => {
    if (!subrecipeQuery) return true;
    // Match on the subrecipe itself or on any of its ingredient/nested names.
    const ingredientNames = sub.ingredients.map(ing =>
      ing.nestedSubrecipeId ? (subrecipeNameById.get(ing.nestedSubrecipeId) ?? '') : (ing.productName ?? '')
    );
    return [sub.name, sub.description, ...ingredientNames]
      .some(field => field?.toLowerCase().includes(subrecipeQuery));
  });

  // Export the subrecipes currently shown (search already applied), with their
  // contents priced the same way the cards (and the detailed cost price export) are.
  const buildSubrecipesExportContext = (): SubrecipesExportContext => ({
    orderHistory: orderHistoryStore.orderHistoryList,
    customIngredients,
    subrecipesById,
    showExVat: showExVatOrders,
    defaultWastePercentage,
    restaurantName: selectedRestaurant?.name ?? '',
    generatedAt: new Date(),
  });

  const handleExportSubrecipesCsv = () => {
    const ctx = buildSubrecipesExportContext();
    downloadCsv(
      buildSubrecipesCsv(filteredSubrecipes, t, ctx),
      subrecipesExportFilename(ctx.restaurantName, 'csv', ctx.generatedAt)
    );
  };

  const handleExportSubrecipesTxt = () => {
    const ctx = buildSubrecipesExportContext();
    downloadTextFile(
      buildSubrecipesTxt(filteredSubrecipes, t, ctx),
      subrecipesExportFilename(ctx.restaurantName, 'txt', ctx.generatedAt),
      'text/plain'
    );
  };

  // This tab manages revenue (cost-price) categories only; addon-scope BYO CHOICE
  // categories are edited elsewhere and must not appear here or in the reorder payload.
  const revenueCategories = categories.filter(c => (c.scope ?? 'revenue') === 'revenue');
  // Only non-default categories are draggable; default(s) render (edit-only) below.
  const nonDefaultCategories = revenueCategories.filter(c => !c.isDefault);
  const defaultCategories = revenueCategories.filter(c => c.isDefault);

  // A single custom-ingredient card, shared by the desktop list and the mobile
  // load-more card list so both stay in sync.
  const renderCustomIngredientCard = (ingredient: CustomIngredient) => (
    <div
      key={ingredient.id}
      onClick={() => setEditingCustomIngredient(ingredient)}
      className={cn(cardClass, "p-4 cursor-pointer hover:shadow transition-shadow")}
    >
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(mediumText, primaryText)}>
              {ingredient.productName}
            </span>
            {ingredient.category && (
              <span
                className={cn(
                  smallTextSize,
                  mediumText,
                  roundedLG,
                  "px-2 py-1 whitespace-nowrap flex-shrink-0"
                )}
                style={getCategoryStyles(ingredient.category, darkMode)}
              >
                {formatCategoryName(ingredient.category)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteCustomIngredient(ingredient.id, ingredient.productName); }}
            className={cn(
              smallTextSize,
              mediumText,
              "px-3 py-2 flex items-center justify-center rounded-xl border border-[#D92D20] dark:border-red-500 hover:bg-[#FEF3F2] dark:hover:bg-red-500/10 text-[#D92D20] dark:text-textRed-dark transition-colors"
            )}
            aria-label="Delete ingredient"
          >
            {t('costPrice.subrecipes.removeAction')}
          </button>
        </div>
      </div>
      <div className={cn(smallTextSize, tertiaryText, regularText, "mb-1")}>
        {ingredient.articleId} • {ingredient.customPlatformName || ingredient.platform}
      </div>
      <div className={cn(smallTextSize, tertiaryText, regularText)}>
        {ingredient.quantityInfo.stdAmount} {ingredient.quantityInfo.stdUnit} • {formatCustomIngredientPrice(ingredient)}
      </div>
    </div>
  );

  // A single subrecipe card, shared by the desktop grid and the mobile
  // load-more card list.
  const renderSubrecipeCard = (subrecipe: Subrecipe) => {
    const totalCost = calculateSubrecipeCost(subrecipe);
    // Cost per standard unit of yield (per portion, per g, or per ml).
    const perPortion = subrecipe.portions > 0 ? totalCost / subrecipe.portions : 0;
    // g/ml yields are shown scaled to /kg and /L (same convention as
    // regular & custom ingredients) instead of an unreadable €0.0125/g.
    const isWeightOrVolumeYield = subrecipe.portionsUnit === 'g' || subrecipe.portionsUnit === 'ml';
    // Ingredient + nested-subrecipe names for the at-a-glance contents list.
    const ingredientNames = subrecipe.ingredients
      .map(ing =>
        ing.nestedSubrecipeId
          ? (subrecipeNameById.get(ing.nestedSubrecipeId) ?? t('costPrice.subrecipes.deletedSubrecipeLabel'))
          : (ing.productName ?? '')
      )
      .filter(Boolean);
    const shownIngredients = ingredientNames.slice(0, 5);
    const remainingIngredients = ingredientNames.length - shownIngredients.length;
    return (
      <div
        key={subrecipe.id}
        onClick={() => setEditingSubrecipeId(subrecipe.id)}
        className={cn(cardClass, "p-4 cursor-pointer hover:shadow transition-shadow flex flex-col")}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <span className={cn(mediumText, primaryText, "min-w-0 truncate")}>{subrecipe.name}</span>
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteSubrecipe(subrecipe.id, subrecipe.name); }}
            className={cn(smallTextSize, mediumText, "flex-shrink-0 px-3 py-1.5 flex items-center justify-center rounded-xl border border-[#D92D20] dark:border-red-500 hover:bg-[#FEF3F2] dark:hover:bg-red-500/10 text-[#D92D20] dark:text-textRed-dark transition-colors")}
            aria-label="Delete subrecipe"
          >
            {t('costPrice.subrecipes.removeAction')}
          </button>
        </div>

        {/* Label chips: yield, total cost, cost per portion. Kept
            directly under the title so chips align across cards
            regardless of whether a description is present. */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          <span className="text-xs px-2 py-0.5 rounded-md font-medium bg-[#F2F4F7] text-[#344054] dark:bg-zinc-800 dark:text-zinc-300">
            {t('costPrice.subrecipes.yieldLabel', { portions: subrecipe.portions, unit: getPortionsUnitDisplay(subrecipe.portionsUnit) })}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-md font-medium bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400">
            {t('costPrice.subrecipes.totalCostChip', { cost: totalCost > 0 ? eur(totalCost) : 'N/A' })}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-md font-medium bg-[#F2F4F7] text-[#344054] dark:bg-zinc-800 dark:text-zinc-300">
            {perPortion <= 0
              ? t('costPrice.subrecipes.perPortionChip', { cost: 'N/A' })
              : isWeightOrVolumeYield
                ? formatSubrecipeUnitPrice(perPortion, subrecipe.portionsUnit)
                : t('costPrice.subrecipes.perPortionChip', { cost: eur(perPortion) })}
          </span>
        </div>

        {subrecipe.description && (
          <div className={cn(smallTextSize, tertiaryText, regularText, "mb-2 line-clamp-2")}>
            {subrecipe.description}
          </div>
        )}

        {/* Ingredient contents (max 5, then +N more) */}
        <div className="mt-auto space-y-0.5">
          {shownIngredients.map((name, index) => (
            <div key={index} className={cn(smallTextSize, tertiaryText, regularText, "truncate")}>
              {name}
            </div>
          ))}
          {remainingIngredients > 0 && (
            <div className={cn(smallTextSize, tertiaryText, regularText)}>
              {t('costPrice.subrecipes.moreIngredients', { count: remainingIngredients })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isAllRestaurants) {
    return (
      <div>
        <MobilePageHeader
          title={t('costPrice.manageIngredients.title')}
          right={<RestaurantSelector />}
        />
        <div className="mb-6 mt-4 lg:mt-0">
          <div className="flex items-center mb-4">
            <ArrowLeft
              className="w-6 h-6 mr-2 cursor-pointer"
              onClick={() => navigate('/costprice')}
            />
            <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, "hidden lg:block")}>
              {t('costPrice.manageIngredients.title')}
            </h1>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('costPrice.create.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <MobilePageHeader
        title={t('costPrice.manageIngredients.title')}
        right={<RestaurantSelector />}
      />
      {/* Header */}
      <div className="mb-6 mt-4 lg:mt-0">
        <div className="flex items-center mb-4">
          <ArrowLeft
            className="w-6 h-6 mr-2 cursor-pointer"
            onClick={() => navigate('/costprice')}
          />
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, "hidden lg:block")}>
            {t('costPrice.manageIngredients.title')}
          </h1>
        </div>
      </div>
      
      {/* Resumable subrecipe import review banner */}
      {hasResumableSubrecipeReview && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 text-sm">
            <ClipboardCheck className="h-5 w-5 flex-shrink-0" />
            <span>
              {t('costPrice.subrecipeReview.continueBanner', {
                done: reviewedSubrecipeCount,
                total: subrecipeReviewReport!.subrecipes.length,
              })}
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/costprice/subrecipe-review')}
            className="flex-shrink-0 px-4 py-2 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {t('costPrice.subrecipeReview.continueButton')}
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
          {error}
        </div>
      )}
      
      {isLoading && (
        <div className="text-center py-8">
          <div className="text-muted dark:text-muted-dark">Loading...</div>
        </div>
      )}
      
      {!isLoading && (
        <div className="space-y-6">
          <TabbedSections
            options={[
              { value: 'subrecipes', label: t('costPrice.manageIngredients.subrecipes') },
              { value: 'customIngredients', label: t('costPrice.manageIngredients.customIngredients') },
              { value: 'categories', label: t('costPrice.manageIngredients.categories') },
            ]}
            value={activeTab}
            onChange={(v) => setActiveTab(v as 'subrecipes' | 'customIngredients' | 'categories')}
            isTimeOptionSelection={false}
          />

          {/* Custom Ingredients Panel */}
          {activeTab === 'customIngredients' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className={cn(extraLargeTextSize, primaryText, semiBoldText)}>
                  {t('costPrice.manageIngredients.customIngredients')}
                </span>
                <span className={cn(smallTextSize, tertiaryText, regularText)}>
                  ({customIngredientQuery ? `${filteredCustomIngredients.length}/${customIngredients.length}` : customIngredients.length})
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <ListSearchInput
                  value={customIngredientSearch}
                  onChange={setCustomIngredientSearch}
                  placeholder={t('costPrice.manageIngredients.searchCustomIngredientsPlaceholder')}
                />
                {canImportPriceFile && (
                  <button
                    onClick={() => setShowUploadPriceFileModal(true)}
                    className={cn(
                      smallTextSize,
                      semiBoldText,
                      'text-darkGreenColor dark:text-darkGreenColor-dark',
                      'border border-darkGreenColor dark:border-darkGreenColor-dark',
                      'rounded-xl',
                      'px-3 py-2',
                      'hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10',
                      'flex items-center gap-2',
                      'w-full sm:w-auto justify-center'
                    )}
                  >
                    <Upload className="w-4 h-4" />
                    <span>{t('costPrice.importSource.entryButton')}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowCreateCustomIngredientModal(true)}
                  className={cn(
                    smallTextSize,
                    'text-white dark:text-white',
                    semiBoldText,
                    'bg-darkGreenColor dark:bg-darkGreenColor-dark',
                    'rounded-xl',
                    'px-3 py-2',
                    'hover:bg-darkGreenColor/95',
                    'flex items-center gap-2',
                    'w-full sm:w-auto justify-center'
                  )}
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('costPrice.createCustomIngredient.title')}</span>
                </button>
              </div>
            </div>

            <div className="space-y-3">
                {supplierNames.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className={cn(smallTextSize, tertiaryText, regularText)}>
                      {t('costPrice.manageIngredients.filterBySupplier')}
                    </span>
                    <select
                      value={supplierFilter}
                      onChange={(e) => setSupplierFilter(e.target.value)}
                      className={cn(
                        smallTextSize, primaryText,
                        'px-3 py-2 border rounded-lg bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                      )}
                    >
                      <option value="all">{t('costPrice.manageIngredients.allSuppliers')}</option>
                      {supplierNames.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {filteredCustomIngredients.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div
                      className={cn(
                        tertiaryText,
                        regularText,
                        smallTextSize,
                        "text-center max-w-md"
                      )}
                    >
                      {customIngredientQuery
                        ? t('costPrice.manageIngredients.noCustomIngredientsMatch')
                        : t('costPrice.manageIngredients.noCustomIngredients')}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Desktop: full list (unchanged). */}
                    <div className="hidden sm:block space-y-3">
                      {filteredCustomIngredients.map(renderCustomIngredientCard)}
                    </div>
                    {/* Mobile: incremental load-more so the DOM never holds the whole list. */}
                    <div className="sm:hidden">
                      <MobileCardList
                        items={filteredCustomIngredients.slice(0, customVisibleCount)}
                        keyOf={(ingredient) => ingredient.id}
                        renderCard={renderCustomIngredientCard}
                        onLoadMore={() => setCustomVisibleCount((v) => v + MOBILE_BATCH)}
                        hasMore={customVisibleCount < filteredCustomIngredients.length}
                        loadMoreLabel={t('costPrice.loadMore')}
                      />
                    </div>
                  </>
                )}
              </div>
          </div>
          )}

          {/* Subrecipes Panel */}
          {activeTab === 'subrecipes' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className={cn(extraLargeTextSize, primaryText, semiBoldText)}>
                  {t('costPrice.manageIngredients.subrecipes')}
                </span>
                <span className={cn(smallTextSize, tertiaryText, regularText)}>
                  ({subrecipeQuery ? `${filteredSubrecipes.length}/${subrecipes.length}` : subrecipes.length})
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <ListSearchInput
                  value={subrecipeSearch}
                  onChange={setSubrecipeSearch}
                  placeholder={t('costPrice.manageIngredients.searchSubrecipesPlaceholder')}
                />
                {canExportSubrecipes && subrecipes.length > 0 && (
                  <div ref={exportMenuRef} className="relative w-full sm:w-auto">
                    <button
                      onClick={() => setShowExportMenu(prev => !prev)}
                      aria-haspopup="menu"
                      aria-expanded={showExportMenu}
                      className={cn(
                        smallTextSize,
                        semiBoldText,
                        'text-darkGreenColor dark:text-darkGreenColor-dark',
                        'border border-darkGreenColor dark:border-darkGreenColor-dark',
                        'rounded-xl',
                        'px-3 py-2',
                        'hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10',
                        'flex items-center gap-2',
                        'w-full sm:w-auto justify-center'
                      )}
                    >
                      <Download className="w-4 h-4" />
                      <span>{t('costPrice.subrecipesExport.exportButton')}</span>
                    </button>
                    {showExportMenu && (
                      <div
                        role="menu"
                        className="absolute right-0 top-full mt-1 min-w-[220px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setShowExportMenu(false); handleExportSubrecipesCsv(); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          {t('costPrice.export.exportCsv')}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setShowExportMenu(false); handleExportSubrecipesTxt(); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark"
                        >
                          <FileText className="w-4 h-4" />
                          {t('costPrice.subrecipesExport.exportTxt')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {canImportSubrecipeFile && (
                  <button
                    onClick={() => setShowUploadSubrecipeTemplateModal(true)}
                    className={cn(
                      smallTextSize,
                      semiBoldText,
                      'text-darkGreenColor dark:text-darkGreenColor-dark',
                      'border border-darkGreenColor dark:border-darkGreenColor-dark',
                      'rounded-xl',
                      'px-3 py-2',
                      'hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10',
                      'flex items-center gap-2',
                      'w-full sm:w-auto justify-center'
                    )}
                  >
                    <Upload className="w-4 h-4" />
                    <span>{t('costPrice.uploadSubrecipeTemplate.entryButton')}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowCreateSubrecipeModal(true)}
                  className={cn(
                    smallTextSize,
                    'text-white dark:text-white',
                    semiBoldText,
                    'bg-darkGreenColor dark:bg-darkGreenColor-dark',
                    'rounded-xl',
                    'px-3 py-2',
                    'hover:bg-darkGreenColor/95',
                    'flex items-center gap-2',
                    'w-full sm:w-auto justify-center'
                  )}
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('costPrice.createSubrecipe.title')}</span>
                </button>
              </div>
            </div>
            
            {filteredSubrecipes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className={cn(tertiaryText, regularText, smallTextSize, "text-center max-w-md")}>
                  {subrecipeQuery
                    ? t('costPrice.manageIngredients.noSubrecipesMatch')
                    : t('costPrice.manageIngredients.noSubrecipes')}
                </div>
              </div>
            ) : (
              <>
                {/* Desktop: full responsive grid (unchanged). */}
                <div className="hidden sm:grid grid-cols-1 min-[650px]:grid-cols-2 min-[1050px]:grid-cols-3 gap-4">
                  {filteredSubrecipes.map(renderSubrecipeCard)}
                </div>
                {/* Mobile: incremental load-more so the DOM never holds the whole list. */}
                <div className="sm:hidden">
                  <MobileCardList
                    items={filteredSubrecipes.slice(0, subrecipeVisibleCount)}
                    keyOf={(subrecipe) => subrecipe.id}
                    renderCard={renderSubrecipeCard}
                    onLoadMore={() => setSubrecipeVisibleCount((v) => v + MOBILE_BATCH)}
                    hasMore={subrecipeVisibleCount < filteredSubrecipes.length}
                    loadMoreLabel={t('costPrice.loadMore')}
                  />
                </div>
              </>
            )}
          </div>
          )}

          {/* Categories Panel */}
          {activeTab === 'categories' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className={cn(extraLargeTextSize, primaryText, semiBoldText)}>
                  {t('costPrice.manageIngredients.categories')}
                </span>
                <span className={cn(smallTextSize, tertiaryText, regularText)}>
                  ({revenueCategories.length})
                </span>
              </div>
            </div>

            <div className="space-y-3">
                {/* Create a new category */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newCategoryName.trim()) handleCreateCategory(); }}
                    placeholder={t('costPrice.categoryAssignmentModal.enterNewCategoryName')}
                    className={cn(
                      smallTextSize, primaryText,
                      "flex-1 min-w-0 px-4 py-2 border rounded-xl bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                    )}
                  />
                  <button
                    onClick={handleCreateCategory}
                    disabled={!newCategoryName.trim()}
                    className={cn(
                      smallTextSize, semiBoldText,
                      "px-4 py-2 flex items-center justify-center text-white dark:text-white bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl hover:bg-darkGreenColor/95 disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                    aria-label="Create category"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {revenueCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div
                      className={cn(
                        tertiaryText,
                        regularText,
                        smallTextSize,
                        "text-center max-w-md"
                      )}
                    >
                      {t('costPrice.manageIngredients.noCategories')}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Non-default categories: draggable to reorder */}
                    <SortableList
                      items={nonDefaultCategories}
                      getId={(c) => c.id}
                      onReorder={handleReorderCategories}
                      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                      <div className="space-y-3">
                        {nonDefaultCategories.map(category => (
                          <SortableCategoryCard
                            key={category.id}
                            category={category}
                            onEdit={() => setEditingCategory({ id: category.id, name: category.name, color: category.color })}
                            onDelete={() => setDeletingCategory({ id: category.id, name: category.name })}
                          />
                        ))}
                      </div>
                    </SortableList>

                    {/* Default categories: edit-only, not draggable, not deletable */}
                    {defaultCategories.map(category => (
                      <div
                        key={category.id}
                        className={cn(cardClass, "p-4")}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div
                              className="w-4 h-4 rounded flex-shrink-0 border border-gray-200 dark:border-gray-700"
                              style={{ backgroundColor: category.color || '#2563EB' }}
                              title={`Color: ${category.color || '#2563EB'}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className={cn(mediumText, primaryText)}>
                                {category.name}
                                <span className={cn(smallTextSize, tertiaryText, regularText, "ml-2")}>
                                  (Default)
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setEditingCategory({ id: category.id, name: category.name, color: category.color })}
                              className={cn(
                                "w-9 h-9 flex items-center justify-center border border-[1px] border-[#0E8965] dark:border-darkGreenColor-dark",
                                "rounded-full bg-white dark:bg-card-dark transition-colors",
                                "hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10"
                              )}
                              aria-label="Edit category"
                            >
                              <PencilLine className="w-4 h-4" color={darkMode ? iconGreenAccent.dark : iconGreenAccent.light} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
          </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateCustomIngredientModal && restaurantId && (
        <CustomIngredientDetailModal
          ingredient={null}
          restaurantId={restaurantId}
          onClose={() => setShowCreateCustomIngredientModal(false)}
          onSaved={handleIngredientsReloaded}
        />
      )}
      
      {showUploadPriceFileModal && restaurantId && canImportPriceFile && (
        <UploadSupplierPriceFileModal
          restaurantId={restaurantId}
          onClose={() => setShowUploadPriceFileModal(false)}
          onIngredientsCreated={handleIngredientsReloaded}
        />
      )}

      {showUploadSubrecipeTemplateModal && restaurantId && canImportSubrecipeFile && (
        <UploadSubrecipeTemplateModal
          key={restaurantId}
          restaurantId={restaurantId}
          onClose={() => setShowUploadSubrecipeTemplateModal(false)}
          onCompleted={handleIngredientsReloaded}
        />
      )}

      {showCreateSubrecipeModal && restaurantId && (
        <SubrecipeDetailModal
          subrecipeId={null}
          onClose={() => setShowCreateSubrecipeModal(false)}
          onSaved={handleSubrecipeSaved}
        />
      )}

      {editingCustomIngredient && restaurantId && (
        <CustomIngredientDetailModal
          ingredient={editingCustomIngredient}
          restaurantId={restaurantId}
          onClose={() => setEditingCustomIngredient(null)}
          onSaved={handleIngredientsReloaded}
        />
      )}

      {editingSubrecipeId && restaurantId && (
        <SubrecipeDetailModal
          subrecipeId={editingSubrecipeId}
          onClose={() => setEditingSubrecipeId(null)}
          onSaved={handleSubrecipeSaved}
        />
      )}
      
      {/* Delete Custom Ingredient Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!deletingCustomIngredient}
        title="Delete Custom Ingredient"
        message={`Are you sure you want to delete "${deletingCustomIngredient?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Ingredient"
        cancelLabel="Cancel"
        onConfirm={handleDeleteCustomIngredientConfirm}
        onCancel={() => setDeletingCustomIngredient(null)}
      />

      {/* Delete Subrecipe Entity Modal — checks usage and cascades on confirm */}
      {deletingSubrecipe && (
        <DeleteSubrecipeEntityModal
          subrecipeId={deletingSubrecipe.id}
          subrecipeName={deletingSubrecipe.name}
          onClose={() => setDeletingSubrecipe(null)}
          onDeleted={handleSubrecipeDeleted}
        />
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <EditCategoryModal
          isOpen={!!editingCategory}
          categoryId={editingCategory.id}
          categoryName={editingCategory.name}
          categoryColor={editingCategory.color}
          onCategoryNameChange={(name) => setEditingCategory({ ...editingCategory, name })}
          onCategoryColorChange={(color) => setEditingCategory({ ...editingCategory, color })}
          onClose={() => setEditingCategory(null)}
          onSave={handleCategoryUpdate}
        />
      )}

      {/* Delete Category Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!deletingCategory}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${deletingCategory?.name}"? Items in this category will be moved to the default category.`}
        confirmLabel="Delete Category"
        cancelLabel="Cancel"
        onConfirm={handleDeleteCategoryConfirm}
        onCancel={() => setDeletingCategory(null)}
      />
    </div>
  );
};

export default ManageIngredientsPage;
